import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Checkbox, Icon, Loader, Modal } from 'semantic-ui-react';
import { useToasts } from 'common/toast';
import { useSelector } from 'react-redux';
import PositionedModal from 'common/components/PositionedModal';
import { getCurrentOrganizationId, getOrganizationMembers } from 'common/selectors/auth';
import { HeaderCell, Table, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { H2, PageHeader, PageSection } from 'common/styles/page';
import { StyledButton, StyledCancelButton } from 'common/components/button/StyledButton';

import { useImportEmployeesMutation } from '../../../../features/employee/employeeApi';

const ImportMemberReviewData: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { addToast } = useToasts();

  const { results, file } = location.state || {};
  const organizationId = useSelector(getCurrentOrganizationId) as number;
  const organizationMembers = useSelector(getOrganizationMembers);
  const hasExistingOrganizationMembers = Array.isArray(organizationMembers) && organizationMembers.length > 0;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [importEmployees] = useImportEmployeesMutation();

  if (!results || !file) {
    return <p>Missing import data. Return to import step.</p>;
  }

  const headers = results.columns || [];
  const invalid_rows = results.invalid_rows || [];
  const verified_rows = results.verified_rows || [];
  const hasFlaggedRows = !results.all_valid;
  const hasValidRows = verified_rows.length > 0;

  const getCellStatusProps = (status: string) => {
    switch (status) {
      case 'valid':
        return { positive: true };
      case 'warning':
        return { warning: true };
      case 'error':
        return { negative: true };
      default:
        return {};
    }
  };

  const proceedWithImport = async (mode: 'insert' | 'update') => {
    setConfirmOpen(false);
    setIsSubmitting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await importEmployees({
        organizationId,
        mode: mode === 'insert' ? 1 : 2,
        body: formData as any,
      }).unwrap();

      addToast('Members imported successfully', {
        appearance: 'success',
        autoDismiss: true,
      });

      navigate('/app/team-management');
    } catch (err) {
      console.error('Import failed:', err);
      addToast('Failed to import members', {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProceed = () => {
    if (hasExistingOrganizationMembers) {
      setConfirmOpen(true);
    } else {
      proceedWithImport('insert');
    }
  };

  return (
    <PageSection>
      <PageHeader
        title="Review Member Data"
        controls={
          <>
            <StyledCancelButton onClick={() => navigate(-1)} disabled={isSubmitting} />
            <StyledButton
              onClick={handleProceed}
              disabled={isSubmitting}
              label="Proceed with Import"
              color={hasFlaggedRows ? 'red' : 'green'}
            />
          </>
        }
      />

      <PageSection
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        {!hasFlaggedRows ? (
          <PageSection display="flex" flexDirection="column">
            <p style={{ color: 'green', fontWeight: 'bold' }}>
              <Icon name="check circle" style={{ marginLeft: 6 }} color={'green'} />
              {results.total_valid_rows} members verified and ready for processing.
            </p>
          </PageSection>
        ) : (
          <p style={{ color: 'red', fontWeight: 'bold' }}>
            <Icon name="warning circle" style={{ marginLeft: 6 }} color={'red'} />
            {results.total_warnings} warnings and {results.total_errors} errors were found in the
            CSV import file.
          </p>
        )}

      </PageSection>

      { hasFlaggedRows && (<>
      <H2 padtop>Flagged Rows</H2>
      <Table celled compact>
        <TableHeader>
          <TableRow>
            <HeaderCell>Row</HeaderCell>
            {headers.map((col: any) => (
              <HeaderCell key={col.name}>
                {col.name.replace(/_/g, ' ').replace(/\b\w/g, (char: string) => char.toUpperCase())}
              </HeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {invalid_rows.map((row: any, rowIndex: number) => (
            <TableRow
              key={rowIndex}
              warning={row.row_status === 'warning'}
              negative={row.row_status === 'error'}
            >
              <TableCell>{row.row_number}</TableCell>
              {headers.map((col: any) => {
                const cell = row.fields?.[col.name];
                const status = cell?.status || 'valid';
                const cellProps = getCellStatusProps(status);
                return (
                  <TableCell key={col.name} {...cellProps} title={cell?.error || ''}>
                    {status !== 'valid' && (
                      <Icon
                        name="warning circle"
                        style={{ marginLeft: 6 }}
                        color={status === 'error' ? 'red' : 'yellow'}
                      />
                    )}
                    {cell?.value || ''}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </>
      )}

      {hasValidRows && (
        <>
          <H2 padtop>Verified Rows</H2>
          <Table celled compact>
            <TableHeader>
              <TableRow>
                <HeaderCell>Row</HeaderCell>
                {headers.map((col: any) => (
                  <HeaderCell key={col.name}>
                    {col.name.replace(/_/g, ' ').replace(/\b\w/g, (char: string) => char.toUpperCase())}
                  </HeaderCell>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {verified_rows.map((row: any, rowIndex: number) => (
                <TableRow
                  key={rowIndex}
                  warning={row.row_status === 'warning'}
                  negative={row.row_status === 'error'}
                >
                  <TableCell>{row.row_number}</TableCell>
                  {headers.map((col: any) => {
                    const cell = row.fields?.[col.name];
                    const status = cell?.status || 'valid';
                    const cellProps = getCellStatusProps(status);
                    return (
                      <TableCell key={col.name} {...cellProps} title={cell?.error || ''}>
                        {status !== 'valid' && (
                          <Icon
                            name="warning circle"
                            style={{ marginLeft: 6 }}
                            color={status === 'error' ? 'red' : 'yellow'}
                          />
                        )}
                        {cell?.value || ''}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      <PositionedModal open={confirmOpen} onClose={() => setConfirmOpen(false)} size="small" closeIcon>
        <Modal.Header>Existing members</Modal.Header>
        <Modal.Content>
          <p>
            If any existing members were not included in the import file, how should they be handled?
          </p>
        </Modal.Content>
        <Modal.Actions style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
          <StyledButton
            label="Cancel"
            color="gray"
            onClick={() => setConfirmOpen(false)}
            disabled={isSubmitting}
          />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <StyledButton
              label="Keep members"
              color="blue"
              onClick={() => proceedWithImport('insert')}
              disabled={isSubmitting}
            />
            <StyledButton
              label="Archive members"
              color="green"
              onClick={() => proceedWithImport('update')}
              disabled={isSubmitting}
            />
          </div>
        </Modal.Actions>
      </PositionedModal>
    </PageSection>
  );
};

export default ImportMemberReviewData;
